// Проверка и загрузка нового веб-слоя.
//
// Порядок: спросить манифест у сервера → сравнить версию с работающей → если новее,
// скачать бандл (плагин сам сверит sha256) → перевести стор в `ready`. Применение —
// отдельным шагом по кнопке пользователя (см. useAppUpdate).

import type { components } from '@/shared/api/generated/schema';
import { httpClient } from '@/shared/api';

import { useAppUpdateStore } from './appUpdateStore';
import { currentVersion, downloadBundle, updatesSupported } from './updater';
import { isNewer } from './version';

type Manifest = components['schemas']['WebRelease'];

const MANIFEST_PATH = '/platform/web-release/latest/';

let inFlight = false;

/**
 * Одна проверка обновления. Идемпотентна и защищена от параллельного запуска:
 * триггеров несколько (монтирование слоя, возврат сети), а смысл один.
 *
 * Ничего не бросает: обновление — фоновая история, и её сбой не должен всплывать
 * пользователю. Неуспех оставляет стор в `error` или `idle`, следующая проверка
 * попробует снова.
 */
export async function checkForUpdate(): Promise<void> {
  if (!updatesSupported() || inFlight) return;

  const store = useAppUpdateStore.getState();
  // Уже что-то скачано и ждёт применения — повторная проверка ничего не улучшит.
  if (store.phase === 'ready' || store.phase === 'applying') return;

  inFlight = true;
  store.setPhase('checking');
  try {
    // 204 → тела нет; httpClient вернёт пустой ответ, дальше просто нечего сравнивать.
    const manifest = await httpClient.get<Manifest | null>(MANIFEST_PATH);
    if (!manifest || !manifest.version) {
      store.setPhase('idle');
      return;
    }

    const running = await currentVersion();
    if (running && !isNewer(manifest.version, running)) {
      store.setPhase('idle');
      return;
    }

    store.setPhase('downloading');
    const bundle = await downloadBundle({
      url: manifest.url,
      version: manifest.version,
      checksum: manifest.checksum,
    });
    store.setReady({ version: bundle.version, bundleId: bundle.id });
  } catch (error) {
    console.warn('Проверка обновления веб-слоя не удалась', error);
    store.setPhase('error');
  } finally {
    inFlight = false;
  }
}
